import { Popover } from 'module/ui/components/atoms/popover';
import moment from 'moment';
import { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';

const TOC_VISIBLE_LINES = 20;

const ToCContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  justify-content: center;
  align-items: start;
  position: relative;
  z-index: 1;
`;

const ToCLine = styled.div<{ isActive: boolean }>`
  width: 12px;
  height: 1px;
  background-color: ${(props) =>
    props.isActive ? 'var(--button-primary)' : 'var(--gray-30)'};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.isActive ? 1 : 0.5)};
  &:hover {
    opacity: 1;
    background-color: var(--button-primary);
  }
`;

const PopoverContent = styled.div`
  max-height: 50vh;
  overflow-y: auto;
  min-width: 200px;
  max-width: 300px;
  padding: var(--spacing-04);
`;

const PopoverTitle = styled.div`
  font-size: var(--font-size-sm);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-02);
`;

const PopoverItem = styled.div<{ isActive: boolean }>`
  padding: var(--spacing-02) var(--spacing-03);
  cursor: pointer;
  color: ${(props) =>
    props.isActive ? 'var(--link-color)' : 'var(--text-tertiary)'};
  transition: all 0.2s ease;
  font-size: var(--font-size-sm);
  &:hover {
    background-color: var(--gray-10);
    color: var(--text-primary);
    border-radius: var(--border-radius-xsmall);
  }
`;

interface MonthTOCProps {
  groupBy: string;
  onMonthSelect?: (monthKey: string) => void;
  selectedMonthKey?: string;
  billingStartDate?: string; // YYYY-MM-DD format, defaults to 2020-04-01
  useCustomWeeks?: boolean; // Use custom weeks (1-7, 8-14) instead of ISO weeks
  title?: string;
}

export const MonthTOC: React.FC<MonthTOCProps> = ({
  groupBy,
  onMonthSelect,
  selectedMonthKey,
  billingStartDate,
  useCustomWeeks = false,
  title,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // Generate periods (months/weeks/days) from current period (upper cap) down to April 2020 (lower cap)
  const periodItems = useMemo(() => {
    const periods: Array<{ key: string; label: string; date: moment.Moment }> =
      [];

    // Lower cap: April 2020 (or billing start date if provided and later than April 2020)
    const defaultStartDate = moment('2020-04-01');
    const billingDate = billingStartDate ? moment(billingStartDate) : null;
    // Use the later date between billing date and April 2020 as the lower cap
    const startDate =
      billingDate && billingDate.isAfter(defaultStartDate)
        ? billingDate
        : defaultStartDate;

    switch (groupBy) {
      case 'WEEK': {
        if (useCustomWeeks) {
          // Custom weeks: 1-7, 8-14, etc. of each month
          const currentDate = moment();
          const startMonth = startDate.clone().startOf('month');

          let date = currentDate.clone().startOf('month');

          while (date.isSameOrAfter(startMonth, 'month')) {
            const monthStart = date.clone().startOf('month');
            const daysInMonth = date.daysInMonth();
            // const currentDay = date.isSame(moment(), 'month')
            //   ? moment().date()
            //   : daysInMonth;

            // Generate weeks for this month (in reverse order)
            for (let weekNum = 5; weekNum >= 1; weekNum--) {
              const weekStartDay = (weekNum - 1) * 7 + 1;
              const weekEndDay = Math.min(weekNum * 7, daysInMonth);

              if (weekStartDay > daysInMonth || weekEndDay < 1) {
                continue;
              }

              const weekStart = monthStart.clone().date(weekStartDay);
              const weekEnd = monthStart.clone().date(weekEndDay);

              // Only add if this week is after or equal to start date
              if (weekStart.isBefore(startDate)) {
                break; // No need to check earlier weeks in this month
              }

              // Allow future weeks to be displayed (removed the future date check)

              const year = date.year();
              const month = date.month() + 1;

              let label: string;

              if (weekStart.month() === weekEnd.month()) {
                label = `${weekStart.format('MMM D')} - ${weekEnd.format('D')}`;
              } else {
                label = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`;
              }

              periods.push({
                key: `${year}-${month.toString().padStart(2, '0')}-W${weekNum}`,
                label,
                date: weekStart.clone(),
              });
            }

            date.subtract(1, 'month');
          }
        } else {
          // ISO weeks
          const currentDate = moment().startOf('week');
          const startWeek = startDate.clone().startOf('week');

          let date = currentDate.clone();

          while (date.isSameOrAfter(startWeek, 'week')) {
            const weekStart = date.clone().startOf('week');
            const weekEnd = date.clone().endOf('week');

            // Format label: "Dec 1 - Dec 7" or "Nov 30 - Dec 6"
            let label: string;

            if (weekStart.month() === weekEnd.month()) {
              label = `${weekStart.format('MMM D')} - ${weekEnd.format('D')}`;
            } else {
              label = `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D')}`;
            }

            periods.push({
              key: date.format('YYYY-[W]WW'),
              label,
              date: date.clone(),
            });
            date.subtract(1, 'week');
          }
        }

        break;
      }
      case 'QUARTER': {
        // Upper cap: current quarter
        const currentDate = moment().startOf('quarter');
        const startQuarter = startDate.clone().startOf('quarter');

        let date = currentDate.clone();

        while (date.isSameOrAfter(startQuarter, 'quarter')) {
          const quarterNum = Math.floor(date.month() / 3) + 1;

          periods.push({
            key: `${date.year()}-Q${quarterNum}`,
            label: `Q${quarterNum} ${date.format('YYYY')}`,
            date: date.clone(),
          });
          date.subtract(1, 'quarter');
        }

        break;
      }
      case 'MONTH':
      default: {
        // Upper cap: current month
        const currentDate = moment().startOf('month');
        const startMonth = startDate.clone().startOf('month');

        let date = currentDate.clone();

        while (date.isSameOrAfter(startMonth, 'month')) {
          periods.push({
            key: date.format('YYYY-MM'),
            label: date.format('MMM YYYY'),
            date: date.clone(),
          });
          date.subtract(1, 'month');
        }

        break;
      }
    }

    return periods;
  }, [groupBy, billingStartDate, useCustomWeeks]);

  const scrollToPeriod = useCallback(
    (periodKey: string) => {
      if (onMonthSelect) {
        onMonthSelect(periodKey);
      }
      setIsPopoverOpen(false);
    },
    [onMonthSelect],
  );

  // Only show first 20 items as lines
  const visibleLineItems = useMemo(
    () => periodItems.slice(0, TOC_VISIBLE_LINES),
    [periodItems],
  );

  // Popover content with all items
  const popoverContent = useMemo(
    () => (
      <PopoverContent>
        {title && <PopoverTitle>{title}</PopoverTitle>}
        {periodItems.map((item) => (
          <PopoverItem
            key={item.key}
            isActive={selectedMonthKey === item.key}
            onClick={() => scrollToPeriod(item.key)}
          >
            {item.label}
          </PopoverItem>
        ))}
      </PopoverContent>
    ),
    [periodItems, selectedMonthKey, scrollToPeriod, title],
  );

  if (periodItems.length === 0) {
    return null;
  }

  return (
    <Popover
      content={popoverContent}
      trigger="hover"
      placement="right"
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      overlayStyle={{ zIndex: 1000 }}
      mouseEnterDelay={0}
      mouseLeaveDelay={0.1}
      align={{ offset: [-30, 0] }}
    >
      <div className="table-of-contents">
        <ToCContainer>
          {visibleLineItems.map((item) => (
            <ToCLine
              key={item.key}
              isActive={selectedMonthKey === item.key}
              onClick={() => scrollToPeriod(item.key)}
              title={item.label}
            />
          ))}
        </ToCContainer>
      </div>
    </Popover>
  );
};
